
import React from 'react';
import { synczstyle } from '@/utils/synczstyle';
import { GlassCard } from '../../../components/ui/GlassCard';
import { Megaphone, Loader2, Plus, AlertCircle, Clock, Link as LinkIcon, Trash2 } from 'lucide-react';
import { Announcement, Service } from '../../../types';

interface PortfolioAnnouncementsProps {
    announcements: Announcement[];
    newAnnouncement: { content: string, linkedServiceId: string, scheduledStart: string, scheduledEnd: string, bgColor: string };
    setNewAnnouncement: React.Dispatch<React.SetStateAction<{ content: string, linkedServiceId: string, scheduledStart: string, scheduledEnd: string, bgColor: string }>>;
    myServices: Service[];
    posting: boolean;
    onPost: () => void;
    onDelete: (id: string) => void;
}

export const ANNOUNCEMENT_COLORS = [
    { id: 'PURPLE', name: 'Indigo / Purple', class: 'bg-gradient-to-r from-indigo-900 via-purple-900 to-indigo-900', preview: 'bg-purple-600' },
    { id: 'BLUE', name: 'Deep Ocean Blue', class: 'bg-gradient-to-r from-blue-900 via-indigo-900 to-blue-900', preview: 'bg-blue-600' },
    { id: 'EMERALD', name: 'Emerald Green', class: 'bg-gradient-to-r from-emerald-900 via-teal-900 to-emerald-900', preview: 'bg-emerald-600' },
    { id: 'AMBER', name: 'Warm Gold / Amber', class: 'bg-gradient-to-r from-amber-950 via-amber-900 to-amber-950', preview: 'bg-amber-600' },
    { id: 'ROSE', name: 'Crimson / Red', class: 'bg-gradient-to-r from-rose-950 via-red-900 to-rose-950', preview: 'bg-rose-600' },
    { id: 'ZINC', name: 'Sleek Dark Zinc', class: 'bg-gradient-to-r from-neutral-900 via-zinc-900 to-neutral-900', preview: 'bg-neutral-600' },
];

export const PortfolioAnnouncements: React.FC<PortfolioAnnouncementsProps> = ({
    announcements, newAnnouncement, setNewAnnouncement, myServices, posting, onPost, onDelete
}) => {
    return (
        <GlassCard>
            <div className="flex items-center gap-4 mb-6 pb-4 border-b border-white/5">
                <Megaphone size={18} className="text-white" />
                <div>
                    <h3 className="text-sm font-bold text-white uppercase tracking-widest">Announcements</h3>
                    <p className="text-[10px] text-neutral-500 mt-1">Share news or promos (Max 3 active)</p>
                </div>
            </div>

            {/* Create Announcement */}
            <div className="bg-white/5 p-4 rounded-sm border border-white/5 mb-6">
                <div className="space-y-3">
                    <label className="text-[10px] text-neutral-500 uppercase tracking-widest block">New Announcement</label>
                    <textarea 
                        className="glass-input w-full p-3 rounded-sm h-20 text-xs" 
                        placeholder="Enter your message..." 
                        value={newAnnouncement.content}
                        onChange={e => setNewAnnouncement({ ...newAnnouncement, content: e.target.value })}
                    />

                    {/* Announcement Color Picker */}
                    <div>
                        <label className="text-[9px] text-neutral-500 uppercase block mb-1.5 font-bold tracking-wider">
                            Banner Color Theme
                        </label>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {ANNOUNCEMENT_COLORS.map(c => {
                                const isSelected = (newAnnouncement.bgColor || 'PURPLE') === c.id;
                                return (
                                    <button
                                        key={c.id}
                                        type="button"
                                        onClick={() => setNewAnnouncement({ ...newAnnouncement, bgColor: c.id })}
                                        className={`flex items-center gap-2 p-2 rounded-sm border text-xs text-left transition-all ${
                                            isSelected 
                                                ? 'border-white bg-white/10 text-white font-bold' 
                                                : 'border-white/10 text-neutral-400 hover:border-white/30 hover:text-white bg-black/20'
                                        }`}
                                    >
                                        <span className={`w-3 h-3 rounded-full shrink-0 ${c.preview} border border-white/20`} />
                                        <span className="text-[10px] truncate">{c.name}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                        <div>
                            <label className="text-[9px] text-neutral-500 uppercase block mb-1">Start (Optional)</label>
                            <input type="datetime-local" className="glass-input w-full p-2 rounded-sm text-xs" value={newAnnouncement.scheduledStart} onChange={e => setNewAnnouncement({ ...newAnnouncement, scheduledStart: e.target.value })} />
                        </div>
                        <div>
                            <label className="text-[9px] text-neutral-500 uppercase block mb-1">End (Optional)</label>
                            <input type="datetime-local" className="glass-input w-full p-2 rounded-sm text-xs" value={newAnnouncement.scheduledEnd} onChange={e => setNewAnnouncement({ ...newAnnouncement, scheduledEnd: e.target.value })} />
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                        <select 
                            className="glass-input flex-1 p-2 rounded-sm text-xs"
                            value={newAnnouncement.linkedServiceId}
                            onChange={e => setNewAnnouncement({ ...newAnnouncement, linkedServiceId: e.target.value })}
                        >
                            <option value="" className="bg-neutral-900 text-white">-- Link a Service (Optional) --</option>
                            {myServices.map(s => (
                                <option key={s.id} value={s.id} className="bg-neutral-900 text-white">{s.title}</option>
                            ))}
                        </select>
                        <button 
                            onClick={onPost}
                            disabled={posting || !newAnnouncement.content || announcements.length >= 3}
                            className={synczstyle('primary')}
                        >
                            {posting ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Post
                        </button>
                    </div>
                    {announcements.length >= 3 && (
                        <div className="flex items-center gap-2 text-[10px] text-orange-400 mt-1">
                            <AlertCircle size={10} /> Limit reached. Delete an old announcement to post a new one.
                        </div>
                    )}
                </div>
            </div>

            {/* List Announcements */}
            <div className="space-y-3">
                {announcements.length === 0 && (
                    <div className="text-center text-[10px] text-neutral-600 italic py-4">No active announcements.</div>
                )}
                {announcements.map(ann => {
                    const colorObj = ANNOUNCEMENT_COLORS.find(c => c.id === (ann.bgColor || 'PURPLE')) || ANNOUNCEMENT_COLORS[0];
                    return (
                        <div key={ann.id} className="flex justify-between items-start p-3 bg-black/40 rounded-sm border border-white/5 group">
                            <div className="flex-1 pr-4">
                                <div className="flex justify-between items-start mb-1.5 gap-2">
                                    <div className="flex items-center gap-2">
                                        <span className={`text-[8px] font-bold uppercase px-1.5 py-0.5 rounded ${ann.active ? 'bg-green-900/30 text-green-400' : 'bg-yellow-900/30 text-yellow-500'}`}>
                                            {ann.active ? 'LIVE' : 'SCHEDULED'}
                                        </span>
                                        <span className="text-[8px] uppercase tracking-wider text-neutral-400 flex items-center gap-1 bg-white/5 px-1.5 py-0.5 rounded border border-white/5">
                                            <span className={`w-2 h-2 rounded-full ${colorObj.preview}`} />
                                            {colorObj.name}
                                        </span>
                                    </div>
                                    {ann.scheduledEnd && (
                                        <span className="text-[8px] text-neutral-500 flex items-center gap-1">
                                            <Clock size={8} /> Ends {new Date(ann.scheduledEnd).toLocaleDateString()}
                                        </span>
                                    )}
                                </div>
                                <p className="text-xs text-neutral-200 leading-relaxed">{ann.content}</p>
                                <div className="flex items-center gap-3 mt-2 text-[10px] text-neutral-500">
                                    <span>Posted: {new Date(ann.createdAt).toLocaleDateString()}</span>
                                    {ann.linkedServiceName && (
                                        <span className="flex items-center gap-1 text-blue-400">
                                            <LinkIcon size={10} /> {ann.linkedServiceName}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <button onClick={() => onDelete(ann.id)} className={synczstyle('danger')}>
                                <Trash2 size={14} />
                            </button>
                        </div>
                    );
                })}
            </div>
        </GlassCard>
    );
};
